import { useEffect, useState } from 'react'
import { Picker } from '../Picker'
import { messageOf } from '../../lib/errors'
import type { OrgUnitOption } from '../../lib/admin'
import { ASSIGNABLE_ROLES, loadJobTitles } from '../../lib/roles'
import {
  functionName,
  HOLDER_STATUS_LABEL,
  holderRoleAllowed,
  loadFunctions,
  loadSetup,
  loadTypicalHolders,
  saveTypicalHolder,
  setTypicalHolderStatus,
  waitingWords,
  type JobFunction,
  type TypicalHolder,
  type TypicalHolderDraft,
  type WaitingFunction,
} from '../../lib/roleSuggestions'

/**
 * Typical holders (154): who each role is for, in words and as a match.
 *
 * Written by administrators (B1; One drafting them is B2). A typical holder is
 * a draft until somebody confirms it, and a draft suggests nobody. Every
 * change — an edit, a confirmation, a retirement — is a new revision on the
 * server, so the answers already given keep the words they were answers to.
 *
 * Tenant and HR Administrator are the tenant administrator's to describe
 * (137); the server refuses anyone else, and this list does not offer them.
 */
export function TypicalHolders({
  orgUnits,
  canPrivileged,
  initial,
}: {
  orgUnits: OrgUnitOption[]
  canPrivileged: boolean
  /** The render suite's way in: effects do not run under renderToString. */
  initial?: {
    holders: TypicalHolder[]
    functions: JobFunction[]
    titles: string[]
    editing?: TypicalHolderDraft | null
    waiting?: WaitingFunction[]
  }
}) {
  const [holders, setHolders] = useState<TypicalHolder[] | null>(initial?.holders ?? null)
  const [functions, setFunctions] = useState<JobFunction[]>(initial?.functions ?? [])
  const [titles, setTitles] = useState<string[]>(initial?.titles ?? [])
  // 303. Functions whose sorts wait on a person: a holder naming one matches nobody through it.
  const [waiting, setWaiting] = useState<WaitingFunction[]>(initial?.waiting ?? [])
  const [editing, setEditing] = useState<TypicalHolderDraft | null>(initial?.editing ?? null)
  const [showRetired, setShowRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  async function reload(retired = showRetired) {
    try {
      const [h, f, t, s] = await Promise.all([loadTypicalHolders(retired), loadFunctions(), loadJobTitles(), loadSetup()])
      setHolders(h)
      setFunctions(f)
      setTitles(t.map((x) => x.title))
      setWaiting(s.waiting)
      setError(null)
    } catch (e) {
      setError(messageOf(e))
    }
  }
  useEffect(() => {
    if (!initial) void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function run(token: string, fn: () => Promise<unknown>) {
    setBusy(token)
    setError(null)
    try {
      await fn()
      await reload()
      return true
    } catch (e) {
      setError(messageOf(e))
      return false
    } finally {
      setBusy(null)
    }
  }

  const mayWrite = (roleKey: string) => canPrivileged || !ASSIGNABLE_ROLES.find((r) => r.key === roleKey)?.privileged

  if (editing) {
    return (
      <HolderEditor
        draft={editing}
        orgUnits={orgUnits}
        functions={functions.filter((f) => !f.retired)}
        titles={titles}
        canPrivileged={canPrivileged}
        busy={busy}
        error={error}
        onCancel={() => setEditing(null)}
        onSave={async (d) => {
          if (await run('save', () => saveTypicalHolder(d))) setEditing(null)
        }}
      />
    )
  }

  return (
    <div className="holders">
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
      <p className="admin__muted">
        Each role says who it is for. A confirmed typical holder suggests the people who match it and do not already hold
        the role; its reason is printed on every suggestion and beside the role in the grant panel. A draft suggests
        nobody.
      </p>
      <div className="holders__bar">
        <button type="button" className="btn btn--primary btn--sm" onClick={() => setEditing(blankDraft())}>
          Write a typical holder
        </button>
        <label className="holders__retired">
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(e) => {
              setShowRetired(e.target.checked)
              void reload(e.target.checked)
            }}
          />
          Show retired
        </label>
      </div>
      {holders !== null && holders.length === 0 && <p className="empty">No typical holders yet.</p>}
      <ul className="holders__list">
        {(holders ?? []).map((h) => (
          <li key={h.holderId} className={`holders__item holders__item--${h.status}`}>
            <div className="holders__head">
              <strong>{h.nameEn}</strong>
              <span className="rolechip">
                {h.roleName}
                {h.scopeTo === 'entity' && <span className="chip__scope">their own legal entity</span>}
              </span>
              <span className={`statuspill statuspill--${h.status === 'confirmed' ? 'ok' : h.status === 'retired' ? 'muted' : 'pending'}`}>
                {HOLDER_STATUS_LABEL[h.status]}
              </span>
              {h.matches !== null && (
                <span className="admin__muted">
                  {h.matches} {h.matches === 1 ? 'person matches' : 'people match'}, {h.signedIn} signed in
                </span>
              )}
            </div>
            <p className="holders__desc">{h.descriptionEn}</p>
            <p className="holders__why">Why: {h.whyEn}</p>
            <p className="admin__muted holders__match">{matchWords(h, functions, orgUnits)}</p>
            {h.status !== 'retired' &&
              waiting
                .filter((w) => h.functions.includes(w.functionKey))
                .map((w) => (
                  <p key={w.functionKey} className="admin__muted holders__waiting">
                    {waitingWords(w)}
                  </p>
                ))}
            {h.roleKey === 'support.hr' && (
              <p className="admin__muted">The HR lane is on hold (HLP-20), so this suggests nobody until it opens.</p>
            )}
            {mayWrite(h.roleKey) ? (
              <div className="holders__acts">
                {h.status === 'draft' && (
                  <button type="button" className="btn btn--primary btn--xs" disabled={busy !== null}
                    onClick={() => run(`c:${h.holderId}`, () => setTypicalHolderStatus(h.holderId, 'confirmed'))}>
                    Confirm
                  </button>
                )}
                {h.status !== 'retired' && (
                  <button type="button" className="btn btn--quiet btn--xs" disabled={busy !== null}
                    onClick={() => setEditing(toDraft(h))}>
                    Edit
                  </button>
                )}
                {h.status === 'confirmed' && (
                  <button type="button" className="btn btn--quiet btn--xs" disabled={busy !== null}
                    onClick={() => run(`d:${h.holderId}`, () => setTypicalHolderStatus(h.holderId, 'draft'))}>
                    Back to draft
                  </button>
                )}
                {h.status !== 'retired' ? (
                  <button type="button" className="btn btn--quiet btn--xs" disabled={busy !== null}
                    onClick={() => run(`r:${h.holderId}`, () => setTypicalHolderStatus(h.holderId, 'retired'))}>
                    Retire
                  </button>
                ) : (
                  <button type="button" className="btn btn--quiet btn--xs" disabled={busy !== null}
                    onClick={() => run(`b:${h.holderId}`, () => setTypicalHolderStatus(h.holderId, 'draft'))}>
                    Bring back as a draft
                  </button>
                )}
              </div>
            ) : (
              <p className="rolerow__locked">Only a Tenant Administrator writes who {h.roleName} is for.</p>
            )}
            <p className="admin__muted holders__rev">
              Revision {h.revision}
              {h.writtenByName ? ` · last changed by ${h.writtenByName}` : ' · drafted from the role design'} ·{' '}
              {new Date(h.writtenAt).toLocaleDateString()}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** "Titles: Marketing Lead, Marketing Manager · every entity". */
export function matchWords(
  h: Pick<TypicalHolder, 'titles' | 'functions' | 'departments' | 'legalEntities' | 'excludedTitles'>,
  functions: Pick<JobFunction, 'key' | 'labelEn' | 'labelEs'>[],
  orgUnits: Pick<OrgUnitOption, 'orgUnitId' | 'name'>[],
): string {
  const unit = (id: string) => orgUnits.find((o) => o.orgUnitId === id)?.name ?? 'a unit'
  const parts: string[] = []
  if (h.titles.length) parts.push(`Titles: ${h.titles.join(', ')}`)
  if (h.functions.length)
    parts.push(`Functions: ${h.functions.map((k) => (() => { const f = functions.find((x) => x.key === k); return f ? functionName(f) : k })()).join(', ')}`)
  if (h.departments.length) parts.push(`Departments: ${h.departments.map(unit).join(', ')}`)
  parts.push(h.legalEntities && h.legalEntities.length ? `In ${h.legalEntities.map(unit).join(', ')}` : 'Every entity')
  if (h.excludedTitles.length) parts.push(`Not: ${h.excludedTitles.join(', ')}`)
  return parts.join(' · ')
}

function blankDraft(): TypicalHolderDraft {
  return {
    holderId: null, roleKey: '', scopeTo: 'none',
    nameEn: '', nameEs: null, descriptionEn: '', descriptionEs: null, whyEn: '', whyEs: null,
    titles: [], functions: [], departments: [], legalEntities: null, excludedTitles: [], excludedEntities: [],
  }
}

function toDraft(h: TypicalHolder): TypicalHolderDraft {
  return {
    holderId: h.holderId, roleKey: h.roleKey, scopeTo: h.scopeTo,
    nameEn: h.nameEn, nameEs: h.nameEs, descriptionEn: h.descriptionEn, descriptionEs: h.descriptionEs,
    whyEn: h.whyEn, whyEs: h.whyEs, titles: h.titles, functions: h.functions, departments: h.departments,
    legalEntities: h.legalEntities, excludedTitles: h.excludedTitles, excludedEntities: h.excludedEntities,
  }
}

export function HolderEditor({
  draft,
  orgUnits,
  functions,
  titles,
  canPrivileged,
  busy,
  error,
  onCancel,
  onSave,
}: {
  draft: TypicalHolderDraft
  orgUnits: OrgUnitOption[]
  functions: JobFunction[]
  titles: string[]
  canPrivileged: boolean
  busy: string | null
  error: string | null
  onCancel: () => void
  onSave: (d: TypicalHolderDraft) => void
}) {
  const [d, setD] = useState<TypicalHolderDraft>(draft)
  const set = <K extends keyof TypicalHolderDraft>(k: K, v: TypicalHolderDraft[K]) => setD((x) => ({ ...x, [k]: v }))
  const roles = ASSIGNABLE_ROLES.filter((r) => holderRoleAllowed(r.key) && (canPrivileged || !r.privileged))
  const role = ASSIGNABLE_ROLES.find((r) => r.key === d.roleKey)
  const departments = orgUnits.filter((o) => o.type === 'department')
  const entities = orgUnits.filter((o) => o.type === 'legal_entity')
  const titleOptions = [...new Set([...titles, ...d.titles, ...d.excludedTitles])].map((t) => ({ value: t, label: t }))
  const ready = d.roleKey && d.nameEn.trim() && d.descriptionEn.trim() && d.whyEn.trim()
    && d.titles.length + d.functions.length + d.departments.length > 0

  return (
    <form
      className="holders__editor"
      onSubmit={(e) => {
        e.preventDefault()
        onSave(d)
      }}
    >
      <p className="admin__muted">
        {d.holderId
          ? 'Saving makes a new revision. Answers already given keep the words they answered; this keeps its status.'
          : 'A new typical holder starts as a draft and suggests nobody until someone confirms it.'}
      </p>
      <div className="admin__fields">
        <label className="admin__field">
          <span className="admin__searchlabel">Role</span>
          <select value={d.roleKey} disabled={d.holderId !== null}
            onChange={(e) => setD((x) => ({ ...x, roleKey: e.target.value, scopeTo: 'none' }))}>
            <option value="">Choose…</option>
            {roles.map((r) => (
              <option key={r.key} value={r.key}>{r.name}</option>
            ))}
          </select>
        </label>
        {role?.scopable && (
          <label className="admin__field">
            <span className="admin__searchlabel">Granted for</span>
            <select value={d.scopeTo} onChange={(e) => set('scopeTo', e.target.value as 'none' | 'entity')}>
              <option value="none">The whole company</option>
              <option value="entity">Their own legal entity</option>
            </select>
          </label>
        )}
        <label className="admin__field">
          <span className="admin__searchlabel">Name</span>
          <input value={d.nameEn} placeholder="Field sales leaders" onChange={(e) => set('nameEn', e.target.value)} />
        </label>
      </div>
      <label className="admin__field holders__wide">
        <span className="admin__searchlabel">Who they are, in a sentence</span>
        <textarea rows={2} value={d.descriptionEn} placeholder="They sell the work to clients."
          onChange={(e) => set('descriptionEn', e.target.value)} />
      </label>
      <label className="admin__field holders__wide">
        <span className="admin__searchlabel">Why they need the role</span>
        <textarea rows={2} value={d.whyEn} placeholder="They need site photos for proposals and client meetings."
          onChange={(e) => set('whyEn', e.target.value)} />
      </label>
      <details className="holders__es">
        <summary>In Spanish (optional)</summary>
        <label className="admin__field">
          <span className="admin__searchlabel">Nombre</span>
          <input lang="es" value={d.nameEs ?? ''} onChange={(e) => set('nameEs', e.target.value || null)} />
        </label>
        <label className="admin__field holders__wide">
          <span className="admin__searchlabel">Quiénes son</span>
          <textarea lang="es" rows={2} value={d.descriptionEs ?? ''} onChange={(e) => set('descriptionEs', e.target.value || null)} />
        </label>
        <label className="admin__field holders__wide">
          <span className="admin__searchlabel">Por qué necesitan el rol</span>
          <textarea lang="es" rows={2} value={d.whyEs ?? ''} onChange={(e) => set('whyEs', e.target.value || null)} />
        </label>
      </details>

      <p className="admin__searchlabel holders__how">How to recognise them — any of these</p>
      <div className="admin__fields">
        <Picker label="Job titles" anyLabel="None" searchPlaceholder="Job title" options={titleOptions}
          selected={d.titles} onChange={(v) => set('titles', v)} />
        <Picker label="Functions (sorted titles)" anyLabel="None" searchPlaceholder="Function"
          options={functions.map((f) => ({ value: f.key, label: functionName(f) }))}
          selected={d.functions} onChange={(v) => set('functions', v)} />
        <Picker label="Departments" anyLabel="None" searchPlaceholder="Department"
          options={departments.map((o) => ({ value: o.orgUnitId, label: o.name }))}
          selected={d.departments} onChange={(v) => set('departments', v)} />
      </div>
      <p className="admin__searchlabel holders__how">Narrow it</p>
      <div className="admin__fields">
        <Picker label="Only in these legal entities" anyLabel="Every entity" searchPlaceholder="Legal entity"
          options={entities.map((o) => ({ value: o.orgUnitId, label: o.name }))}
          selected={d.legalEntities ?? []} onChange={(v) => set('legalEntities', v.length ? v : null)} />
        <Picker label="Never these titles" anyLabel="None" searchPlaceholder="Job title" options={titleOptions}
          selected={d.excludedTitles} onChange={(v) => set('excludedTitles', v)} />
        <Picker label="Never in these entities" anyLabel="None" searchPlaceholder="Legal entity"
          options={entities.map((o) => ({ value: o.orgUnitId, label: o.name }))}
          selected={d.excludedEntities} onChange={(v) => set('excludedEntities', v)} />
      </div>

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
      <div className="admin__inviteactions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy !== null || !ready}>
          {busy === 'save' ? 'Saving…' : d.holderId ? 'Save a new revision' : 'Save as a draft'}
        </button>
        <button type="button" className="btn btn--quiet" disabled={busy !== null} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}
